import Component from '@glimmer/component';
import { htmlSafe } from '@ember/template';

// ---------------------------------------------------------------------------
// SkillChip – inline chip showing a skill name with optional proficiency bar
// ---------------------------------------------------------------------------
//
// IMPORTANT (2026-05-28): unlike Avatar, SkillChip's colors are STATUS,
// not identity. The `@intent` API is `default | primary | success |
// warning`, and the level bar maps level → status (≥80 = success / green,
// ≥60 = brand / violet, else warning / amber). Per the playbook's
// 2026-05-27 categorical-vs-status decision, status colors route through
// the canonical TIO recipes (`--tag-*` / `--success-*` / `--brand` /
// `--warning-*`) rather than a custom palette. We therefore compose on
// top of the shared `.app-chip` system and use the same status tokens
// for the bar fill — no off-brand utilities, dark-mode-correct for free.

interface SkillChipSignature {
  Args: {
    /** Skill name to display */
    name: string;
    /** Proficiency level (0-100) */
    level?: number;
    /** Whether to show the level bar (default: true when level is provided) */
    showLevel?: boolean;
    /** Color intent */
    intent?: 'default' | 'primary' | 'success' | 'warning';
  };
  Element: HTMLDivElement;
}

export default class SkillChip extends Component<SkillChipSignature> {
  get showLevel() {
    return this.args.showLevel !== false && this.args.level !== undefined;
  }

  /** Status intent for the chip body — composes on `.app-chip`. */
  get intentClass() {
    switch (this.args.intent) {
      case 'primary':
        // brand-tinted pill (purple wash) for the headline / category intent.
        return 'app-chip--brand';
      case 'success':
        return 'app-chip--success';
      case 'warning':
        return 'app-chip--warning';
      default:
        return 'app-chip--default';
    }
  }

  /** Status class for the proficiency-level bar fill. */
  get barClass() {
    if (this.args.level === undefined) return 'tio-skill-bar-fill--neutral';
    if (this.args.level >= 80) return 'tio-skill-bar-fill--success';
    if (this.args.level >= 60) return 'tio-skill-bar-fill--brand';
    return 'tio-skill-bar-fill--warning';
  }

  get levelStyle() {
    if (this.args.level === undefined) return htmlSafe('width: 0%');
    return htmlSafe(`width: ${this.args.level}%`);
  }

  <template>
    <div class="app-chip app-chip--lg tio-skill-chip {{this.intentClass}}" ...attributes>
      <span class="tio-skill-chip-name">{{@name}}</span>
      {{#if this.showLevel}}
        <div class="tio-skill-bar">
          <div class="tio-skill-bar-fill {{this.barClass}}" style={{this.levelStyle}}></div>
        </div>
        <span class="tio-skill-chip-level">{{@level}}%</span>
      {{/if}}
    </div>
  </template>
}
